import type { VisualizationBaseCondition } from "@models/VisualizationBase";

/** A widget that references a Dataset-Level Base, tracked for linked-usage counts. */
export interface WidgetBaseUsage {
  widgetId: string;
  dashboardId: string;
}

// A Dataset-Level Base: a reusable respondent population defined once on the
// dataset and referenced by visualizations. Conditions reuse the chart-level
// VisualizationBaseCondition shape (stable question/variable/value IDs, never
// labels). Persisted as an opaque JSON array in the dataset's baseConfigJson
// field — same principle as dateGuardrailsConfigJson (frontend owns the shape,
// backend stores it verbatim).
export interface DatasetBase {
  /** Stable identifier — survives rename and edits; never derived from the name. */
  id: string;
  name: string;
  /** AND-joined conditions; each condition's selections are OR-joined. */
  conditions: VisualizationBaseCondition[];
  /** ISO timestamp of the last save, shown in the catalog's "Last updated" column. */
  updatedAt: string;
  /**
   * Widgets currently referencing this base (source of the "Linked visualizations"
   * count and the save-impact message). Maintained frontend-side on widget
   * save/delete; best-effort, not an authoritative invariant.
   */
  usedWidgets?: WidgetBaseUsage[];
}

export function createBlankDatasetBase(): DatasetBase {
  return {
    id: crypto.randomUUID(),
    name: "",
    conditions: [],
    updatedAt: new Date().toISOString(),
  };
}

/** Case- and whitespace-insensitive key used to enforce unique base names within a dataset. */
export function normalizeBaseName(name: string): string {
  return name.trim().toLowerCase();
}

// Tolerant of both the array form (what we write) and a legacy { bases: [] }
// wrapper, so an older or backend-shaped blob still reads cleanly.
export function parseBaseConfigJson(json?: string | null): DatasetBase[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as DatasetBase[];
    if (parsed && Array.isArray(parsed.bases)) return parsed.bases as DatasetBase[];
    return [];
  } catch {
    console.warn("Failed to parse baseConfigJson");
    return [];
  }
}

export function serializeBaseConfigJson(bases: DatasetBase[]): string {
  return JSON.stringify(bases);
}
